// lib/developerOptions.ts
// Small localStorage-backed developer options store.
// Designed to be extensible: stores a single JSON object.

import { IS_PRODUCTION } from "@/lib/env";
import {
  clearDeveloperOptions as clearDeveloperOptionsFromStorage,
  type DeveloperOptions,
  getDeveloperOptionsEventName,
  loadDeveloperOptions as loadDeveloperOptionsFromStorage,
  saveDeveloperOptions as saveDeveloperOptionsToStorage,
} from "@/lib/storage";

// Re-export the type
export type { DeveloperOptions };

export function loadDeveloperOptions(): DeveloperOptions {
  if (IS_PRODUCTION) {
    return { developerMode: false, version: 1 };
  }
  return loadDeveloperOptionsFromStorage();
}

export function saveDeveloperOptions(opts: DeveloperOptions): void {
  if (IS_PRODUCTION) return;
  saveDeveloperOptionsToStorage(opts);
}

export function getDeveloperOption<T = any>(key: string, fallback?: T): T | undefined {
  const opts = loadDeveloperOptions();
  if (key in opts) return opts[key] as T;
  return fallback;
}

export function setDeveloperOption(key: string, value: any): void {
  if (IS_PRODUCTION) return;

  const opts = loadDeveloperOptions();
  const next = { ...opts, [key]: value } as DeveloperOptions;
  saveDeveloperOptions(next);
}

export function clearDeveloperOptions(): void {
  if (IS_PRODUCTION) return;
  clearDeveloperOptionsFromStorage();
}

/**
 * Get the custom event name for developer options changes.
 * Exported for testing/debugging purposes.
 */
export function getCustomEventName(): string {
  return getDeveloperOptionsEventName();
}
